import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, XCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useT } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingConfirm() {
  const { t } = useT();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | success | error | expired

  useEffect(() => {
    if (!sessionId) {
      // eslint-disable-next-line
      setStatus("error");
      return;
    }
    let attempts = 0;
    const maxAttempts = 8;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts >= maxAttempts) {
          setStatus("error");
          return;
        }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= maxAttempts) {
          setStatus("error");
        } else {
          attempts += 1;
          setTimeout(poll, 2000);
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 lg:px-12 pt-32 md:pt-40 pb-24">
        <div className="bg-white border border-cream-200 rounded-2xl p-10 md:p-14 text-center">
          {status === "polling" && (
            <>
              <Loader2 className="w-12 h-12 mx-auto text-navy-600 animate-spin" />
              <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 display-tight-2 mt-6">{t("conf.polling")}</h1>
              <p className="text-slate-600 mt-4">{t("conf.polling_sub")}</p>
            </>
          )}
          {status === "success" && (
            <>
              <div className="w-16 h-16 rounded-full bg-navy-50 grid place-items-center mx-auto">
                <Check size={26} className="text-navy-700" />
              </div>
              <h1 data-testid="confirm-success" className="text-3xl md:text-4xl font-extrabold text-slate-900 display-tight-2 mt-6">
                {t("conf.success")}
              </h1>
              <p className="text-slate-600 mt-4 max-w-xl mx-auto leading-relaxed">
                {t("conf.success_text")}
              </p>
              <Link
                to="/"
                className="mt-10 inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-cream-50 px-8 py-3.5 text-sm font-medium transition-all hover:-translate-y-0.5"
              >
                {t("conf.back_home")}
              </Link>
            </>
          )}
          {(status === "error" || status === "expired") && (
            <>
              <XCircle className="w-12 h-12 mx-auto text-slate-600" />
              <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 display-tight-2 mt-6">
                {status === "expired" ? t("conf.expired") : t("conf.pending")}
              </h1>
              <p className="text-slate-600 mt-4 max-w-xl mx-auto">
                {t("conf.help")} <a href="mailto:mmspacerents@gmail.com" className="text-navy-700 underline">mmspacerents@gmail.com</a>.
              </p>
              <Link
                to="/prenotazione"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy-600 hover:bg-navy-700 text-white px-7 py-3 text-sm font-medium"
              >
                {t("conf.retry")}
              </Link>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
